/**
 * Shared Arabic vocabulary for the STRUCTURED EARLY CHECK-IN POLICY.
 *
 * Same pattern, and the same reason, as `config/arrivalLabels.js`: agreeing on the data while
 * retyping the sentences is the worse failure of the two — it is invisible in a diff, and the
 * words are what an operator actually acts on. This file already had a live example: the
 * property form and the backend route rejected the same input with two different sentences.
 *
 * The four state values mirror `be/config/earlyCheckIn.js` — that module is the source of truth
 * for the STATES; this one is the source of truth for the WORDS.
 *
 * Arabic-only, per the Arabic-first mandate, and hardcoded rather than routed through `t()`
 * because every surface that renders it is hardcoded Arabic too (see `config/guestRequestLabels.js`
 * for the same call). If these ever join the translated surface, the import boundary is what makes
 * that a small change.
 *
 * Plan: tasks/structured-early-checkin-policy.md · Decisions D153, D154, D155.
 */

/** The field's own name, wherever it is edited or displayed. */
export const EARLY_CHECKIN_FIELD_LABEL = 'سياسة الدخول المبكر';

/** The four states, in the order they are offered. `UNKNOWN` leads because it is the default. */
export const EARLY_CHECKIN_OPTIONS = [
  { value: 'UNKNOWN', label: 'غير محدد' },
  { value: 'NO_EARLY', label: 'لا يوجد دخول مبكر' },
  { value: 'WHEN_READY', label: 'عند جاهزية الشقة' },
  { value: 'NOT_BEFORE', label: 'ليس قبل وقت محدد' }
];

export const EARLY_CHECKIN_VALUES = EARLY_CHECKIN_OPTIONS.map((o) => o.value);

/**
 * What each state MEANS, in the operator's words.
 *
 * The labels alone are short enough to be misread — «عند جاهزية الشقة» could be heard as "the
 * apartment IS ready", which is the one thing this field never says (D155). These lines are the
 * guard against that, and they are why a dropdown is an improvement on prose rather than just a
 * tidier version of it.
 *
 * ⚠️ UNKNOWN's line says the absence out loud. «غير محدد» must never harden into "no early
 * check-in here" in anyone's head — that is D154, and it is a quiet loss that never surfaces as
 * a fault.
 */
export const EARLY_CHECKIN_HELP = {
  UNKNOWN: 'لم تُحدَّد بعد — وهذا لا يعني الرفض.',
  NO_EARLY: 'الدخول دائماً في وقت تسجيل الوصول المعتاد.',
  WHEN_READY: 'لا يوجد حد زمني — الشرط أن تكون الشقة فارغة ونظيفة.',
  NOT_BEFORE: 'لا دخول قبل الوقت المحدد، حتى لو كانت الشقة جاهزة قبله.'
};

/** The D155 line, said once, where the field is edited. */
export const EARLY_CHECKIN_POLICY_NOT_STATE_AR =
  'هذا الحقل يوضّح ما إذا كنا نسمح بالدخول المبكر في هذه الشقة — وليس ما إذا كانت الشقة جاهزة اليوم. الجاهزية تُحسب من إشغال الشقة وحالة النظافة.';

export const EARLY_CHECKIN_TIME_LABEL = 'أقرب وقت للدخول';

/**
 * Rejection wording. ⚠️ These must stay word-for-word identical to the messages in
 * `be/routes/securePropertyRoutes.js` → `applyEarlyCheckInRules`. The route is what makes the rule
 * true; this is only what the operator reads before the request is worth sending. Two sentences for
 * one rejection is how an operator ends up believing there are two different problems.
 */
export const EARLY_CHECKIN_ERRORS = {
  MISSING: 'يرجى تحديد وقت الدخول المبكر',
  NOT_EARLIER: (checkInTime) => `وقت الدخول المبكر يجب أن يكون قبل وقت تسجيل الوصول (${checkInTime})`,
  PROPERTY_TIME_MALFORMED: 'وقت تسجيل الوصول المسجَّل لهذه الشقة غير صالح — صحّحه أولاً من إعدادات الحجز'
};

/** `HH:MM` → minutes, or null. Mirrors `toMinutes` in be/config/earlyCheckIn.js. */
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
export const toMinutes = (value) => {
  if (typeof value !== 'string' || !TIME_PATTERN.test(value)) return null;
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
};

/**
 * Why a NOT_BEFORE pair would be refused, or null when it is fine.
 *
 * Deliberately NOT a string comparison. `'10:00' >= '9:00'` is false, so an unpadded stored
 * check-in hour — and `bookingPageSettings.checkInTime` is an unvalidated string — would pass the
 * client and be rejected by the route, which is the confusing direction to fail in.
 *
 * Mirrors `notBeforeRejectionReason` in be/config/earlyCheckIn.js.
 */
export const earlyCheckInRejection = (notBefore, propertyCheckInTime) => {
  if (toMinutes(propertyCheckInTime) === null) return 'PROPERTY_TIME_MALFORMED';
  if (toMinutes(notBefore) === null) return 'MISSING';
  if (toMinutes(notBefore) >= toMinutes(propertyCheckInTime)) return 'NOT_EARLIER';
  return null;
};
